/**
 * Web useWindowDimensions Hook
 */

import { useEffect, useMemo, useState } from "react";

import type { UseWindowDimensionsOptions, WindowDimensions } from "./types";

const DEFAULT_MOBILE_BREAKPOINT = 768;

function getWindowDimensions() {
  if (typeof window === "undefined") {
    return { width: 0, height: 0 };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

export function useWindowDimensions(options: UseWindowDimensionsOptions = {}): WindowDimensions {
  const { mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT } = options;

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { mediaQuery, isMobile, isDesktop } = useMemo(() => {
    // Desktop from the breakpoint itself, as Tailwind's `md:` (min-width: 768px) and DS-004
    if (windowDimensions.width < mobileBreakpoint) {
      return {
        mediaQuery: "mobile" as const,
        isMobile: true,
        isDesktop: false,
      };
    }
    return {
      mediaQuery: "desktop" as const,
      isMobile: false,
      isDesktop: true,
    };
  }, [windowDimensions.width, mobileBreakpoint]);

  return {
    ...windowDimensions,
    mediaQuery,
    isMobile,
    isDesktop,
  };
}
